import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Editor } from '@tinymce/tinymce-react';
import ArticlePreviewModal from '../../../components/ArticlePreviewModal';
import ArticleCarousel from '../../../components/ArticleCarousel';
import HabboImage from '../../../components/HabboImage';
import ArticleDesk, {
    DeskRibbon,
    DeskPanel,
    DeskField,
    DESK_INPUT_CLASS,
    DESK_BUTTON_CLASS,
    STICKY_BELOW_RIBBON,
} from '../../../components/staff/articles/ArticleDesk';
import { fetchProfileColor, getAvatarBgStyle } from '../../../services/profileColorService';

const SPOTLIGHT_COLORS = [
    { id: 1, name: 'Gold' },
    { id: 2, name: 'Green' },
    { id: 3, name: 'Light Blue' },
    { id: 4, name: 'Maroon' },
    { id: 5, name: 'Pink' },
    { id: 6, name: 'Purple' }
];

const CATEGORY_NAME_TO_SPOTLIGHT_COLOR = {
    'frankly news': 4,
    'habbo news': 1,
    'chickas charity': 5,
    'rare values': 3,
    'frankly guides': 4,
    'guides': 2,
    'competitions': 6,
};

const SITE_MAX_WIDTH = 1400;
const SITE_PAGE_PADDING = 24;
const SITE_GRID_GAP = 24;
const SITE_GRID_COLUMNS = 12;
const ARTICLE_COLUMN_SPAN = 8;
const CONTENT_BOX_BORDER = 1;
const CONTENT_BOX_PADDING = 16;

const SITE_GRID_WIDTH = SITE_MAX_WIDTH - SITE_PAGE_PADDING * 2;
const SITE_COLUMN_WIDTH = (SITE_GRID_WIDTH - SITE_GRID_GAP * (SITE_GRID_COLUMNS - 1)) / SITE_GRID_COLUMNS;
const ARTICLE_BOX_WIDTH = Math.round(
    SITE_COLUMN_WIDTH * ARTICLE_COLUMN_SPAN + SITE_GRID_GAP * (ARTICLE_COLUMN_SPAN - 1)
);
const ARTICLE_BODY_WIDTH = ARTICLE_BOX_WIDTH - (CONTENT_BOX_BORDER + CONTENT_BOX_PADDING) * 2;

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const AUTOSAVE_INTERVAL_MS = 2 * 60 * 1000;

const formatCountdown = (seconds) =>
    `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const EDITOR_CONTENT_STYLE = `
html, body { background: #F2F2F2; }
body {
    font-family: Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.625;
    color: #1f2937;
    margin: 0;
    padding: 0;
}
body > * + * { margin-top: 0.75rem; }
p { margin: 0 0 0.5rem; }
h1, h2, h3 { font-weight: bold; margin: 1rem 0 0.5rem; }
h1 { font-size: 1.5rem; }
h2 { font-size: 1.25rem; }
h3 { font-size: 1.1rem; }
ul { list-style: disc; margin: 0.5rem 0 0.5rem 1.5rem; padding: 0; }
ol { list-style: decimal; margin: 0.5rem 0 0.5rem 1.5rem; padding: 0; }
a { color: #5A2929; text-decoration: underline; }
strong { font-weight: bold; }
em { font-style: italic; }
blockquote {
    border-left: 4px solid #5A2929;
    margin: 1rem 0;
    padding: 0.5rem 0 0.5rem 1rem;
    background-color: #f9f9f9;
    color: #555;
    font-style: italic;
}
pre {
    background-color: #1e1e1e;
    color: #d4d4d4;
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
    margin: 0.75rem 0;
    font-size: 0.85rem;
    line-height: 1.5;
}
code {
    font-family: 'Courier New', Courier, monospace;
    background-color: #1e1e1e;
    color: #d4d4d4;
    padding: 0.15em 0.4em;
    border-radius: 3px;
    font-size: 0.85em;
}
pre code { background: none; padding: 0; border-radius: 0; }
img { max-width: 100%; height: auto; }
table { border-collapse: collapse; }
.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
    color: #9ca3af;
    font-style: italic;
    font-size: 15px;
}
`;

const stripHtml = (html = '') => html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const countWords = (html = '') => {
    const text = stripHtml(html);
    return text ? text.split(/\s+/).length : 0;
};

const toDateInput = (value) => {
    if (!value) return '';
    const s = String(value);
    const parts = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (parts) return `${parts[1]}-${parts[2]}-${parts[3]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const todayInput = () => toDateInput(new Date());

const formatDisplayDate = (value) => {
    const parts = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!parts) return '';
    const d = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
    return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
};

const sourceLabel = (url) => {
    if (!url) return null;
    try {
        return new URL(url).hostname.replace('www.', '');
    } catch (e) {
        return url;
    }
};

const getCategorySpotlightColor = (categoryId, categories) => {
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return null;
    return CATEGORY_NAME_TO_SPOTLIGHT_COLOR[cat.name.toLowerCase()] ?? null;
};

const uploadArticleImage = async (blob, filename = 'image.png') => {
    if (blob.type && !IMAGE_MIME_TYPES.includes(blob.type)) {
        throw new Error('Only JPEG, PNG, GIF and WebP images are allowed');
    }
    if (blob.size > MAX_IMAGE_BYTES) {
        throw new Error('Image must be under 5MB');
    }
    const fd = new FormData();
    fd.append('image', blob, filename);
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await fetch(`${apiUrl}upload`, { method: 'POST', credentials: 'include', body: fd });
    const data = await res.json();
    if (!data.url) throw new Error(data.message || 'Upload failed');
    return data.url;
};

const BLANK_FORM = () => ({
    title: '',
    publicDate: todayInput(),
    categoryId: '',
    source: '',
    headerImage: '',
    articleText: '',
    spotlight: false,
    spotlightColorId: 4,
});

function SuccessModal({ isOpen, onClose, isEditMode }) {
    if (!isOpen) return null;
    const heading = isEditMode ? 'Article Updated!' : 'Thanks for Writing!';
    const body = isEditMode
        ? 'Your article has been published successfully.'
        : 'Your article has been published successfully. Great work!';
    const btnLabel = isEditMode ? 'Done' : 'Create Another';
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
            <div
                className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-md w-full mx-4 z-[101]"
                style={{ boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.5)' }}
            >
                <h2 className="text-maroon uppercase font-['Jersey_10'] text-[17px] tracking-[1px] mb-4">{heading}</h2>
                <hr className="border-gold mb-4" />
                <p className="text-gray-800 font-['Jersey_10'] text-[16px] mb-6">{body}</p>
                <div className="flex justify-end">
                    <button
                        onClick={onClose}
                        className="bg-maroon hover:opacity-90 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-opacity"
                        style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)', backgroundColor: '#5A2929' }}
                    >
                        {btnLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function HeaderImageModal({ isOpen, onClose, onUploaded }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setFile(null);
            setPreview(null);
        }
    }, [isOpen]);

    const reset = () => {
        setFile(null);
        setPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleClose = () => { reset(); onClose(); };

    const pick = (selected) => {
        if (!selected) return;
        setFile(selected);
        setPreview(URL.createObjectURL(selected));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        pick(e.dataTransfer.files[0]);
    };

    const handleUpload = async () => {
        if (!file) { toast.error('Please select a file first'); return; }
        try {
            setUploading(true);
            const url = await uploadArticleImage(file, file.name);
            onUploaded(url);
            toast.success('Header image set');
            handleClose();
        } catch (err) {
            toast.error(err.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />
            <div
                className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-lg w-full mx-4 z-[111]"
                style={{ boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.5)' }}
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-maroon uppercase font-['Jersey_10'] text-[17px] tracking-[1px]">
                        Upload Header Image
                    </h2>
                    <button type="button" onClick={handleClose} className="text-gray-500 hover:text-gray-800 transition-colors text-xl leading-none">&times;</button>
                </div>
                <hr className="border-gold mb-4" />
                <div className="space-y-4">
                    {!preview ? (
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                            className="w-full border-2 border-dashed border-gray-400 rounded bg-gray-50 hover:bg-gray-100 transition-colors py-10 flex flex-col items-center gap-3"
                        >
                            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                            </svg>
                            <span className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-gray-600">Click or drag and drop to upload</span>
                            <span className="text-xs text-gray-400">JPEG, PNG, GIF or WebP, max 5MB</span>
                        </button>
                    ) : (
                        <div className="space-y-3">
                            <div className="border-2 border-gray-300 rounded overflow-hidden bg-gray-100 flex items-center justify-center max-h-48">
                                <img src={preview} alt="Preview" className="max-w-full max-h-48 object-contain" />
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600 truncate max-w-[60%]">{file?.name}</span>
                                <button type="button" onClick={reset} className="text-xs text-gray-500 hover:text-red-600 font-['Jersey_10'] uppercase tracking-wide transition-colors">Remove</button>
                            </div>
                        </div>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={(e) => pick(e.target.files[0])}
                        className="hidden"
                    />
                    {file && (
                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={uploading}
                            className="w-full font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                            style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                        >
                            {uploading ? 'Uploading...' : 'Use This Image'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function ConfirmPublishModal({ isOpen, onConfirm, onCancel }) {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onCancel} />
            <div
                className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-md w-full mx-4 z-[101]"
                style={{ boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.5)' }}
            >
                <h2 className="text-maroon uppercase font-['Jersey_10'] text-[17px] tracking-[1px] mb-4">Publish Article?</h2>
                <hr className="border-gold mb-4" />
                <p className="text-gray-800 font-['Jersey_10'] text-[16px] mb-6">
                    This will make the article visible to everyone. Are you sure it's ready?
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] transition-colors"
                        style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="bg-maroon hover:opacity-90 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] transition-opacity"
                        style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)', backgroundColor: '#5A2929' }}
                    >
                        Yes, Publish
                    </button>
                </div>
            </div>
        </div>
    );
}

function ArticleEditor({ userData, adminMode = false }) {
    const navigate = useNavigate();
    const { articleId } = useParams();
    const isEditMode = !!articleId;

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showConfirmPublish, setShowConfirmPublish] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showCarouselPreview, setShowCarouselPreview] = useState(() => {
        const saved = sessionStorage.getItem('article-editor-carousel-preview');
        return saved !== null ? saved === 'true' : true;
    });
    const [hasSavedAsDraft, setHasSavedAsDraft] = useState(isEditMode || adminMode);
    const [isDraft, setIsDraft] = useState(false);
    const [isOwner, setIsOwner] = useState(true);
    const [authorName, setAuthorName] = useState(userData?.habboName || userData?.username || '');
    const [authorBg, setAuthorBg] = useState('#5A2929');
    const [formData, setFormData] = useState(BLANK_FORM());
    const [headerModalOpen, setHeaderModalOpen] = useState(false);
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
    const [autoSaving, setAutoSaving] = useState(false);
    const [lastAutoSaveAt, setLastAutoSaveAt] = useState(null);
    const [nextAutoSaveAt, setNextAutoSaveAt] = useState(null);
    const [countdown, setCountdown] = useState(0);

    const editorRef = useRef(null);
    const editorHostRef = useRef(null);
    const lastSavedSignatureRef = useRef(null);
    const autoSaveRef = useRef(null);

    const autoResizeEditor = useCallback(() => {
        const editor = editorRef.current;
        if (!editor || editor.removed) return;
        try {
            editor.execCommand('mceAutoResize');
        } catch (e) {}
    }, []);

    const handleEditorInit = useCallback((evt, editor) => {
        editorRef.current = editor;

        const timers = [0, 150, 600].map(delay => setTimeout(autoResizeEditor, delay));
        editor.on('remove', () => timers.forEach(clearTimeout));

        const body = editor.getBody();
        if (body) {
            body.querySelectorAll('img').forEach(img => {
                if (!img.complete) img.addEventListener('load', autoResizeEditor, { once: true });
            });
        }
    }, [autoResizeEditor]);

    useEffect(() => {
        const host = editorHostRef.current;
        if (!host || typeof ResizeObserver === 'undefined') return;

        let lastWidth = Math.round(host.getBoundingClientRect().width);
        const observer = new ResizeObserver(entries => {
            const width = Math.round(entries[0].contentRect.width);
            if (width === lastWidth) return;
            lastWidth = width;
            autoResizeEditor();
        });
        observer.observe(host);

        return () => observer.disconnect();
    }, [autoResizeEditor, loading]);

    const previewArticle = useMemo(() => ({
        id: 'preview',
        title: formData.title || 'Article Title',
        body: stripHtml(formData.articleText),
        headerImage: formData.headerImage,
        spotlightColorId: getCategorySpotlightColor(formData.categoryId, categories) ?? formData.spotlightColorId,
        publicDate: formData.publicDate || todayInput(),
        sortOrder: 0,
    }), [formData.title, formData.articleText, formData.headerImage, formData.categoryId, formData.spotlightColorId, formData.publicDate, categories]);

    const categoryName = useMemo(
        () => categories.find(c => c.id === formData.categoryId)?.name || 'Uncategorised',
        [categories, formData.categoryId]
    );

    const wordCount = useMemo(() => countWords(formData.articleText), [formData.articleText]);

    const editorInit = useMemo(() => ({
        menubar: true,
        plugins: [
            'advlist', 'autolink', 'autoresize', 'lists', 'link', 'image', 'charmap', 'preview',
            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
            'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
        ],
        toolbar: 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image | code',
        toolbar_sticky: true,
        toolbar_sticky_offset: STICKY_BELOW_RIBBON,
        placeholder: 'Type here to start!',
        min_height: 620,
        max_height: 20000,
        autoresize_bottom_margin: 30,
        resize: false,
        branding: false,
        automatic_uploads: true,
        file_picker_types: 'image',
        images_file_types: 'jpeg,jpg,png,gif,webp',
        file_picker_callback: (callback, value, meta) => {
            if (meta.filetype !== 'image') return;
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/jpeg,image/png,image/gif,image/webp';
            input.onchange = () => {
                const file = input.files && input.files[0];
                if (!file) return;
                const dismiss = toast.loading ? toast.loading('Uploading image...') : null;
                uploadArticleImage(file, file.name)
                    .then(url => {
                        callback(url, { alt: file.name.replace(/\.[^.]+$/, '') });
                        if (dismiss !== null) toast.dismiss(dismiss);
                        toast.success('Image uploaded');
                    })
                    .catch(err => {
                        if (dismiss !== null) toast.dismiss(dismiss);
                        toast.error(err.message || 'Upload failed');
                    });
            };
            input.click();
        },
        images_upload_handler: (blobInfo) =>
            uploadArticleImage(blobInfo.blob(), blobInfo.filename()).catch(err => {
                toast.error(err.message || 'Upload failed');
                throw err;
            }),
        content_style: EDITOR_CONTENT_STYLE,
    }), []);

    useEffect(() => {
        if (adminMode) {
            loadCategoriesAndArticleAdmin();
        } else if (isEditMode) {
            loadCategoriesAndArticle();
        } else {
            loadCategories();
        }
    }, []);

    useEffect(() => {
        sessionStorage.setItem('article-editor-carousel-preview', showCarouselPreview);
    }, [showCarouselPreview]);

    useEffect(() => {
        if (!authorName) return;
        let cancelled = false;
        fetchProfileColor(authorName).then(color => {
            if (!cancelled && color) setAuthorBg(color);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [authorName]);

    const isSaveable = Boolean(
        formData.title.trim() && formData.categoryId && String(formData.articleText).trim()
    );
    const editingPublished = isEditMode && !isDraft;
    const canAutoSave = isEditMode && isSaveable;

    useEffect(() => {
        autoSaveRef.current = async () => {
            if (!canAutoSave || submitting || autoSaving || showSuccess || showConfirmPublish) return;

            const asDraft = !editingPublished;
            const signature = JSON.stringify(buildPayload(asDraft));
            if (signature === lastSavedSignatureRef.current) return;

            try {
                setAutoSaving(true);
                const { data } = await persistArticle(asDraft);
                if (!data.success) {
                    toast.error(data.message || 'Auto-save failed');
                    return;
                }
                lastSavedSignatureRef.current = signature;
                setLastAutoSaveAt(new Date());
                if (asDraft) {
                    setIsDraft(true);
                    setHasSavedAsDraft(true);
                }
            } catch {
                toast.error('Auto-save failed');
            } finally {
                setAutoSaving(false);
            }
        };
    });

    useEffect(() => {
        if (!autoSaveEnabled || !canAutoSave) {
            setNextAutoSaveAt(null);
            return;
        }
        setNextAutoSaveAt(Date.now() + AUTOSAVE_INTERVAL_MS);
        const timer = setInterval(() => {
            setNextAutoSaveAt(Date.now() + AUTOSAVE_INTERVAL_MS);
            autoSaveRef.current?.();
        }, AUTOSAVE_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [autoSaveEnabled, canAutoSave]);

    useEffect(() => {
        if (!nextAutoSaveAt) {
            setCountdown(0);
            return;
        }
        const tick = () => setCountdown(Math.max(0, Math.round((nextAutoSaveAt - Date.now()) / 1000)));
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [nextAutoSaveAt]);

    if (!userData || (adminMode ? !userData.hasAdminRole : !userData.hasStaffRole)) return <Navigate to="/" replace />;

    const loadCategories = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const res = await fetch(`${apiUrl}articles/categories`, { credentials: 'include' });
            const data = await res.json();
            if (data.success) setCategories(data.categories);
        } catch {
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    const applyLoadedArticle = (a) => {
        setIsDraft(a.draft || false);
        setAutoSaveEnabled(!!a.draft);
        if (a.author) setAuthorName(a.author);
        setFormData({
            title: a.title || '',
            publicDate: toDateInput(a.publicDate) || todayInput(),
            categoryId: a.categoryId || '',
            source: a.source || '',
            headerImage: a.headerImage || '',
            articleText: a.articleText || '',
            spotlight: a.spotlight || false,
            spotlightColorId: a.spotlightColorId || 4,
        });
    };

    const loadCategoriesAndArticle = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const [catRes, artRes] = await Promise.all([
                fetch(`${apiUrl}articles/categories`, { credentials: 'include' }),
                fetch(`${apiUrl}articles/author/articles/${articleId}`, { credentials: 'include' }),
            ]);
            const catData = await catRes.json();
            const artData = await artRes.json();

            if (catData.success) setCategories(catData.categories);

            if (artData.success) {
                setIsOwner(artData.article.isOwner !== false);
                applyLoadedArticle(artData.article);
            } else {
                toast.error(artData.message || 'Failed to load article');
                navigate('/staff/articles/my-articles');
            }
        } catch {
            toast.error('Failed to load article');
            navigate('/staff/articles/my-articles');
        } finally {
            setLoading(false);
        }
    };

    const loadCategoriesAndArticleAdmin = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const [catRes, artRes] = await Promise.all([
                fetch(`${apiUrl}articles/categories`, { credentials: 'include' }),
                fetch(`${apiUrl}admin/articles/${articleId}/edit`, { credentials: 'include' }),
            ]);
            const catData = await catRes.json();
            const artData = await artRes.json();

            if (catData.success) setCategories(catData.categories);

            if (artData.success) {
                setIsOwner(true);
                applyLoadedArticle(artData.article);
            } else {
                toast.error(artData.message || 'Failed to load article');
                navigate('/staff/articles/management');
            }
        } catch {
            toast.error('Failed to load article');
            navigate('/staff/articles/management');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const buildPayload = (draft) => {
        const spotlightColorId = formData.spotlight
            ? (getCategorySpotlightColor(formData.categoryId, categories) ?? formData.spotlightColorId)
            : null;
        return {
            title: formData.title,
            publicDate: formData.publicDate,
            categoryId: formData.categoryId,
            source: formData.source,
            headerImage: formData.headerImage,
            articleText: formData.articleText,
            spotlight: formData.spotlight ? 1 : 0,
            spotlightColorId,
            draft: draft ? 1 : 0,
        };
    };

    const validate = () => {
        if (!formData.title.trim()) { toast.error('Title is required'); return false; }
        if (!formData.categoryId) { toast.error('Category is required'); return false; }
        if (!formData.articleText.trim()) { toast.error('Article content is required'); return false; }
        return true;
    };

    const persistArticle = async (asDraft) => {
        const apiUrl = process.env.REACT_APP_API_URL;
        const url = adminMode
            ? `${apiUrl}admin/articles/${articleId}`
            : isEditMode
                ? `${apiUrl}articles/author/articles/${articleId}`
                : `${apiUrl}articles`;
        const method = (adminMode || isEditMode) ? 'PUT' : 'POST';
        const body = JSON.stringify(buildPayload(asDraft));

        const res = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body,
        });
        const data = await res.json();
        return { data, signature: body };
    };

    const saveArticle = async (asDraft) => {
        if (!validate()) return;

        try {
            setSubmitting(true);
            const { data, signature } = await persistArticle(asDraft);

            if (data.success) {
                lastSavedSignatureRef.current = signature;
                setLastAutoSaveAt(null);
                if (asDraft) {
                    toast.success('Draft saved!');
                    setIsDraft(true);
                    setHasSavedAsDraft(true);
                    if (!isEditMode && data.articleId) {
                        navigate(`/staff/articles/edit/${data.articleId}`);
                    }
                } else {
                    setIsDraft(false);
                    setShowSuccess(true);
                }
            } else {
                toast.error(data.message || 'Failed to save article');
            }
        } catch {
            toast.error('Failed to save article');
        } finally {
            setSubmitting(false);
        }
    };

    const savePublishedChanges = async () => {
        if (!validate()) return;

        try {
            setSubmitting(true);
            const { data, signature } = await persistArticle(false);
            if (data.success) {
                lastSavedSignatureRef.current = signature;
                setLastAutoSaveAt(null);
                toast.success('Article updated!');
            } else {
                toast.error(data.message || 'Failed to update article');
            }
        } catch {
            toast.error('Failed to update article');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSuccessClose = () => {
        setShowSuccess(false);
        if (adminMode) {
            navigate('/staff/articles/management');
        } else if (isEditMode) {
            navigate('/staff/articles/my-articles');
        } else {
            setHasSavedAsDraft(false);
            setAutoSaveEnabled(false);
            setLastAutoSaveAt(null);
            lastSavedSignatureRef.current = null;
            setFormData(BLANK_FORM());
            if (editorRef.current) editorRef.current.setContent('');
        }
    };

    const autoSpotlightColour = getCategorySpotlightColor(formData.categoryId, categories);
    const canPublish = isEditMode ? isDraft && isOwner : true;
    const publishBlockedReason = !hasSavedAsDraft ? 'Save as draft first before publishing' : '';

    const statusChip = isEditMode
        ? (isDraft ? (isOwner ? 'Draft' : 'Shared draft') : 'Published')
        : 'Unsaved';
    const statusChipClass = isEditMode
        ? (isDraft ? 'bg-yellow-200 text-yellow-900' : 'bg-green-200 text-green-900')
        : 'bg-gray-200 text-gray-700';

    const autoSaveHint = !isEditMode
        ? 'Save as draft once before auto-save can run'
        : !isSaveable
            ? 'Add a title, category and some content first'
            : editingPublished
                ? 'Saves changes straight to the live article every 2 minutes'
                : 'Saves this draft every 2 minutes';

    const autoSaveStatus = autoSaving
        ? 'Auto-saving...'
        : lastAutoSaveAt
            ? `Auto-saved ${lastAutoSaveAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
            : 'Every 2 minutes';
    const autoSaveCountdown = !autoSaving && nextAutoSaveAt ? ` (${formatCountdown(countdown)})` : '';

    const docTab = isEditMode
        ? (formData.title.trim() ? `Editing: ${formData.title.trim()}` : 'Editing Article')
        : null;

    if (loading) {
        return (
            <ArticleDesk userData={userData} docTab={docTab}>
                <DeskPanel title="Article" padding="p-6">
                    <p className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-gray-600">Loading document...</p>
                </DeskPanel>
            </ArticleDesk>
        );
    }

    return (
        <ArticleDesk userData={userData} docTab={docTab}>
            <DeskRibbon>
                <span className={`font-['Jersey_10'] text-[14px] uppercase tracking-[1px] px-2 py-[3px] ${statusChipClass}`}>
                    {statusChip}
                </span>
                <span className="text-[12px] text-gray-500">{wordCount} words</span>
                {isEditMode && isDraft && !isOwner && (
                    <span className="text-[12px] italic text-blue-700">You are a collaborator on this draft</span>
                )}

                <div className="flex-1" />

                <label
                    className={`flex items-center gap-2 ${canAutoSave ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    title={autoSaveHint}
                >
                    <input
                        type="checkbox"
                        checked={autoSaveEnabled && canAutoSave}
                        disabled={!canAutoSave}
                        onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                        className={`h-4 w-4 ${canAutoSave ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}
                    />
                    <span className={`font-['Jersey_10'] text-[14px] uppercase tracking-[1px] ${canAutoSave ? 'text-gray-700' : 'text-gray-400'}`}>
                        Auto-save
                    </span>
                </label>
                {canAutoSave && autoSaveEnabled && (
                    <span className="text-[12px] text-gray-500">{autoSaveStatus}{autoSaveCountdown}</span>
                )}

                <button
                    type="button"
                    onClick={() => setShowPreview(true)}
                    className="bg-blue-600 px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white transition-colors hover:bg-blue-700"
                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                >
                    Preview
                </button>

                {isEditMode && !isDraft ? (
                    <button
                        type="button"
                        onClick={savePublishedChanges}
                        disabled={submitting}
                        className="bg-gray-600 px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white transition-colors hover:bg-gray-700 disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                    >
                        {submitting ? 'Saving...' : 'Save Changes'}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => saveArticle(true)}
                        disabled={submitting}
                        className="bg-gray-600 px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white transition-colors hover:bg-gray-700 disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                    >
                        {submitting ? 'Saving...' : 'Save as Draft'}
                    </button>
                )}

                {canPublish && (
                    <button
                        type="button"
                        onClick={() => setShowConfirmPublish(true)}
                        disabled={submitting || !hasSavedAsDraft}
                        title={publishBlockedReason}
                        className="px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)', backgroundColor: '#5A2929' }}
                    >
                        {submitting ? 'Publishing...' : 'Publish Article'}
                    </button>
                )}
            </DeskRibbon>

            <div className="flex flex-col items-start gap-6 min-[1360px]:flex-row">
                <div className="mx-auto w-full space-y-4 min-[1360px]:mx-0 min-[1360px]:shrink-0" style={{ maxWidth: ARTICLE_BOX_WIDTH }}>
                    <div
                        className="border border-gray-300 bg-offwhite p-4"
                        style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.5)' }}
                    >
                        <div className="space-y-4">
                            <div className="flex items-start gap-3 text-sm tracking-wide text-gray-500">
                                <span
                                    className="relative inline-block h-[50px] w-[50px] flex-shrink-0 overflow-hidden rounded-full border-[2px] border-coal/20"
                                    style={getAvatarBgStyle(authorBg)}
                                >
                                    {authorName && (
                                        <HabboImage
                                            habbo={authorName}
                                            direction={2}
                                            head_direction={3}
                                            gesture="sml"
                                            className="absolute top-[-16px] left-[-10px] block max-w-none"
                                            style={{ imageRendering: 'pixelated' }}
                                        />
                                    )}
                                </span>

                                <div className="min-w-0 flex-1">
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Untitled article"
                                        className="w-full border-0 border-b border-dashed border-gray-300 bg-transparent p-0 pb-[2px] text-[20px] font-bold uppercase leading-tight text-coal_dark placeholder:font-normal placeholder:normal-case placeholder:text-gray-300 focus:border-solid focus:border-maroon focus:outline-none"
                                    />

                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm tracking-wide text-gray-500">
                                        <span>{formatDisplayDate(formData.publicDate) || 'No date set'}</span>
                                        <span>•</span>
                                        <span>{categoryName}</span>
                                        <span>•</span>
                                        <span>By: <span className="font-bold">{authorName || 'You'}</span></span>
                                        {formData.source && (
                                            <>
                                                <span>•</span>
                                                <span>Source: <span className="font-bold">{sourceLabel(formData.source)} ⧉</span></span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {formData.headerImage ? (
                                <div className="relative flex w-full items-center justify-center overflow-y-auto overflow-x-hidden border border-gray-300 bg-gray-200 p-[20px] pb-[40px]">
                                    <img src={formData.headerImage} alt={formData.title} />
                                    {formData.source && (
                                        <span className="absolute bottom-0 right-2 bg-gray-300 px-[10px] py-[6px] text-xs">
                                            Source: {sourceLabel(formData.source)} ⧉
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, headerImage: '' }))}
                                        className="absolute right-2 top-2 bg-black/60 px-2 py-[3px] font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-white transition-colors hover:bg-black/80"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setHeaderModalOpen(true)}
                                    className="flex w-full flex-col items-center gap-1 border border-dashed border-gray-300 bg-gray-100 py-5 transition-colors hover:bg-gray-200"
                                >
                                    <span className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                                        Add a header image
                                    </span>
                                    <span className="text-[11px] text-gray-400">Optional, appears above your first paragraph</span>
                                </button>
                            )}

                            <div ref={editorHostRef} style={{ width: '100%', maxWidth: ARTICLE_BODY_WIDTH }}>
                                <Editor
                                    apiKey={process.env.REACT_APP_TINYMCE_KEY}
                                    onInit={handleEditorInit}
                                    value={formData.articleText}
                                    onEditorChange={(content) => setFormData(prev => ({ ...prev, articleText: content }))}
                                    init={editorInit}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="border border-gray-300 bg-offwhite" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.5)' }}>
                        <div className="flex items-center justify-between gap-2 border-b border-gray-300 bg-gray-100 px-3 py-[5px]">
                            <span className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-maroon">
                                Spotlight Carousel
                            </span>
                            <div className="flex items-center gap-3">
                                {!formData.spotlight && (
                                    <span className="text-[11px] italic text-gray-400">not spotlighted</span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setShowCarouselPreview(p => !p)}
                                    className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-600 transition-colors hover:text-maroon"
                                >
                                    {showCarouselPreview ? 'Hide ▲' : 'Show ▼'}
                                </button>
                            </div>
                        </div>
                        {showCarouselPreview && (
                            <div className="p-3 pb-0">
                                <ArticleCarousel articles={[previewArticle]} />
                            </div>
                        )}
                    </div>
                </div>

                <aside
                    className="w-full space-y-4 min-[1360px]:sticky min-[1360px]:w-[400px] min-[1360px]:shrink-0 min-[1360px]:overflow-y-auto"
                    style={{ top: STICKY_BELOW_RIBBON + 12, maxHeight: `calc(100vh - ${STICKY_BELOW_RIBBON + 24}px)` }}
                >
                    <DeskPanel title="Article Details">
                        <div className="space-y-3">
                            <DeskField label="Title" required>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="Enter article title"
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <div className="grid grid-cols-2 gap-3">
                                <DeskField label="Public Date" required>
                                    <input
                                        type="date"
                                        name="publicDate"
                                        value={formData.publicDate}
                                        onChange={handleInputChange}
                                        className={DESK_INPUT_CLASS}
                                    />
                                </DeskField>
                                <DeskField label="Category" required>
                                    <select
                                        name="categoryId"
                                        value={formData.categoryId}
                                        onChange={handleInputChange}
                                        className={DESK_INPUT_CLASS}
                                    >
                                        <option value="">Select...</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </DeskField>
                            </div>

                            <DeskField label="Source URL" hint="optional">
                                <input
                                    type="url"
                                    name="source"
                                    value={formData.source}
                                    onChange={handleInputChange}
                                    placeholder="https://..."
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <DeskField label="Header Image URL" hint="optional">
                                <div className="flex gap-2">
                                    <input
                                        type="url"
                                        name="headerImage"
                                        value={formData.headerImage}
                                        onChange={handleInputChange}
                                        placeholder="https://..."
                                        className={DESK_INPUT_CLASS}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setHeaderModalOpen(true)}
                                        className={`shrink-0 ${DESK_BUTTON_CLASS}`}
                                    >
                                        Upload
                                    </button>
                                </div>
                            </DeskField>
                        </div>
                    </DeskPanel>

                    <DeskPanel title="Spotlight">
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="spotlight"
                                    name="spotlight"
                                    checked={formData.spotlight}
                                    onChange={handleInputChange}
                                    disabled={!formData.categoryId}
                                    className={`h-5 w-5 rounded ${formData.categoryId ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}
                                />
                                <label
                                    htmlFor="spotlight"
                                    className={`font-['Jersey_10'] text-[15px] uppercase tracking-[1px] ${formData.categoryId ? 'cursor-pointer text-gray-700' : 'cursor-not-allowed text-gray-400'}`}
                                >
                                    Spotlight this article
                                </label>
                            </div>

                            {!formData.categoryId && (
                                <p className="text-[11px] italic text-gray-400">Pick a category first.</p>
                            )}

                            {formData.spotlight && autoSpotlightColour === null && (
                                <DeskField label="Spotlight Colour">
                                    <select
                                        name="spotlightColorId"
                                        value={formData.spotlightColorId}
                                        onChange={handleInputChange}
                                        className={DESK_INPUT_CLASS}
                                    >
                                        {SPOTLIGHT_COLORS.map(color => (
                                            <option key={color.id} value={color.id}>{color.name}</option>
                                        ))}
                                    </select>
                                </DeskField>
                            )}

                            {formData.spotlight && autoSpotlightColour !== null && (
                                <p className="text-[11px] italic text-gray-500">
                                    Colour is set automatically from the {categoryName} category.
                                </p>
                            )}

                            {formData.spotlight && (
                                <p className="text-[11px] text-gray-500">
                                    The carousel preview sits under the page on the left.
                                </p>
                            )}
                        </div>
                    </DeskPanel>
                </aside>
            </div>

            <HeaderImageModal
                isOpen={headerModalOpen}
                onClose={() => setHeaderModalOpen(false)}
                onUploaded={(url) => setFormData(prev => ({ ...prev, headerImage: url }))}
            />
            <SuccessModal isOpen={showSuccess} onClose={handleSuccessClose} isEditMode={isEditMode} />
            <ConfirmPublishModal
                isOpen={showConfirmPublish}
                onConfirm={() => { setShowConfirmPublish(false); saveArticle(false); }}
                onCancel={() => setShowConfirmPublish(false)}
            />
            <ArticlePreviewModal
                isOpen={showPreview}
                onClose={() => setShowPreview(false)}
                formData={formData}
                categories={categories.map(c => ({ id: c.id, name: c.name }))}
                authorName={authorName || 'You'}
            />
        </ArticleDesk>
    );
}

export default ArticleEditor;
